import { AlertTriangle } from 'lucide-react';
import { type ReactNode, useEffect, useRef } from 'react';

import { t } from '../lib/i18n';
import { Button } from './ui';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: ReactNode;
  /** Extra emphasis for irreversible / auction-outcome-changing actions. */
  tone?: 'default' | 'danger';
  confirmLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Modal confirmation for admin actions that cannot be undone from the UI —
 * the monorepo replacement for the web's BidActionConfirmationModal.
 *
 * Every action gated by this dialog also writes an `admin.*` audit event
 * server-side, so the operator's click is recoverable as a record even though
 * the effect is not reversible.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  tone = 'default',
  confirmLabel,
  cancelLabel,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  // Callers pass inline handlers, so `onCancel`/`busy` change on every parent
  // render. Read them through a ref: an effect keyed on them re-ran on each
  // keystroke in a field inside `message` and yanked focus to the confirm
  // button after every letter (the «Запросити документ» note, 2026-10-12).
  const latest = useRef({ busy, onCancel });
  latest.current = { busy, onCancel };

  // Escape closes (not while the action is in flight).
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !latest.current.busy) latest.current.onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Focus lands on the confirm button ONCE, when the dialog opens, so it is
  // operable from the keyboard alone without stealing focus afterwards.
  useEffect(() => {
    if (!open) return;
    const focus = window.setTimeout(() => confirmRef.current?.focus(), 0);
    return () => window.clearTimeout(focus);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-cf-lg border border-cf-line bg-cf-surface p-5 shadow-xl">
        <div className="flex items-start gap-3">
          {tone === 'danger' && (
            <AlertTriangle className="mt-0.5 shrink-0 text-cf-danger" size={20} aria-hidden />
          )}
          <div className="min-w-0">
            <h2 className="text-[17px] font-semibold tracking-[-0.01em] text-cf-ink">{title}</h2>
            <div className="mt-2 text-[14px] leading-[1.5] text-cf-ink-muted">{message}</div>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            {cancelLabel ?? t('common.cancel')}
          </Button>
          <Button
            ref={confirmRef}
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel ?? t('bidDetails.actions.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}
